import { useState, useRef, useEffect } from 'react';
import { AppPage, AppCard } from '../../components/general';
import { Toast } from 'primereact/toast';
import { DataTable, TableDefinition, DataTableSearchCreateHeader } from '../../components/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';
import { BlockUI } from 'primereact/blockui';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { Tenant } from '../../models/tenant';
import TenantService from '../../services/tenantService';
import { EditTenant } from './EditTenant';

export const Tenants = (): JSX.Element => {
  const toast = useRef<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [search, setSearch] = useState<string>('');
  const [editorSidebarVisible, setEditorSidebarVisible] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth < 768);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
  const [confirmationData, setConfirmationData] = useState<Tenant | null>(null);
  const [confirmationOperation, setConfirmationOperation] = useState<'save' | 'delete'>('save');

  const tenantService = TenantService.getInstance();

  useEffect(() => {
    fetchTenants();
  }, []);

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const fetchTenants = (): void => {
    setIsLoading(true);

    tenantService
      .getAll()
      .then((tenants) => {
        tenants.forEach((tenant) => {
          (tenant as any)['urlsString'] = tenant.urls.join(', ');
        });
        setTenants(tenants);
        setIsLoading(false);
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsLoading(false));
  };

  const showMessage = (
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    detail: string,
    life: number = 3000
  ) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life,
    });
  };

  const showError = (message: string) => {
    showMessage('error', 'Error', message, 5000);
  };

  const onEdit = (tenant: Tenant): void => {
    setSelectedTenant(tenant);
    setEditorSidebarVisible(true);
  };

  const onDelete = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setConfirmationOperation('delete');
    setConfirmationData(tenant);
    setConfirmDialogVisible(true);
  };

  const handleConfirmAccept = () => {
    setConfirmDialogVisible(false);

    if (confirmationOperation === 'delete' && confirmationData) {
      deleteTenant(confirmationData);
    } else if (confirmationOperation === 'save' && confirmationData) {
      if (selectedTenant) {
        updateTenant(confirmationData);
      } else {
        createTenant(confirmationData);
      }
    }
  };

  const handleConfirmReject = () => {
    setConfirmDialogVisible(false);
  };

  const deleteTenant = (tenant: Tenant) => {
    setIsDeleting(true);
    tenantService
      .delete(tenant.tenantId!)
      .then(() => {
        showMessage('success', 'Success', `Tenant ${tenant.name} successfully deleted`);
        fetchTenants();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsDeleting(false));
  };

  const createTenant = (tenant: Tenant) => {
    setIsSubmitting(true);
    tenantService
      .create(tenant)
      .then(() => {
        showMessage('success', 'Success', `Tenant ${tenant.name} successfully created`);
        setEditorSidebarVisible(false);
        fetchTenants();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsSubmitting(false));
  };

  const updateTenant = (tenant: Tenant) => {
    setIsSubmitting(true);
    tenantService
      .update(tenant)
      .then(() => {
        showMessage('success', 'Success', `Tenant ${tenant.name} successfully updated`);
        setEditorSidebarVisible(false);
        fetchTenants();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsSubmitting(false));
  };

  const actionTemplate = (tenant: Tenant) => {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          icon="pi pi-eye"
          className="p-button-rounded p-button-text"
          tooltip="Select"
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            // Handle select action
            toast.current?.show({
              severity: 'info',
              summary: 'Selected',
              detail: `Tenant ${tenant.name} selected`,
              life: 3000,
            });
          }}
        />
        <Button
          type="button"
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-success"
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          onClick={() => onEdit(tenant)}
        />
        <Button
          type="button"
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          onClick={() => onDelete(tenant)}
        />
      </div>
    );
  };

  // Define table columns
  const tableDef: TableDefinition<Tenant> = {
    columns: [
      {
        field: 'tenantId',
        header: 'Tenant ID',
        dataType: 'text',
      },
      {
        field: 'name',
        header: 'Name',
        dataType: 'text',
      },
      {
        field: 'clientId',
        header: 'Client ID',
        dataType: 'text',
      },
      {
        field: 'urlsString',
        header: 'URLs',
        dataType: 'text',
      },
      {
        header: 'Actions',
        body: actionTemplate,
        dataType: 'none',
        sortable: false,
        filter: false,
      },
    ],
  };

  const renderContent = () => {
    return (
      <>
        <Toast ref={toast} />
        <ConfirmDialog />

        {/* Custom confirmation dialog for deletes */}
        <Dialog
          header={confirmationOperation === 'save' ? 'Confirm Changes' : 'Confirm Delete'}
          visible={confirmDialogVisible}
          onHide={handleConfirmReject}
          style={{ width: '450px' }}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button label="No" icon="pi pi-times" onClick={handleConfirmReject} className="p-button-text" />
              <Button label="Yes" icon="pi pi-check" onClick={handleConfirmAccept} autoFocus />
            </div>
          }
        >
          <div className="flex align-items-center gap-3">
            <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: 'var(--yellow-500)' }} />
            {confirmationOperation === 'save' ? (
              <span>Are you sure you want to save these changes?</span>
            ) : (
              <span>Are you sure you want to delete this tenant: {confirmationData?.name}?</span>
            )}
          </div>
        </Dialog>

        <Sidebar
          visible={editorSidebarVisible}
          position="right"
          onHide={() => setEditorSidebarVisible(false)}
          className="p-sidebar-md md:p-sidebar-lg"
          style={isSmallScreen ? undefined : { width: '100%', maxWidth: '700px' }}
          header={selectedTenant ? `Edit Tenant - ${selectedTenant.name}` : `Create Tenant`}
          fullScreen={isSmallScreen}
          modal
        >
          <BlockUI blocked={isSubmitting} fullScreen={false} className="relative">
            {isSubmitting && (
              <div
                className="flex align-items-center justify-content-center"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  zIndex: 1,
                }}
              >
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
              </div>
            )}
            <EditTenant
              tenant={selectedTenant}
              onSave={(tenant) => {
                // Set up confirmation data and show dialog
                setConfirmationData(tenant);
                setConfirmationOperation('save');
                setConfirmDialogVisible(true);
              }}
              onCancel={() => setEditorSidebarVisible(false)}
            />
          </BlockUI>
        </Sidebar>

        {isDeleting && (
          <div className="w-full mb-3">
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
            <div className="text-center mt-2 text-sm text-primary">Deleting tenant...</div>
          </div>
        )}

        <DataTable
          header={
            <DataTableSearchCreateHeader
              onCreate={() => {
                setSelectedTenant(null);
                setEditorSidebarVisible(true);
              }}
              onSearchChange={setSearch}
            />
          }
          tableDef={tableDef}
          data={tenants}
          globalFilterValue={search}
          loading={isLoading}
        />
      </>
    );
  };

  return (
    <AppPage title="Tenants" breadcrumbs="Admin - Tenants">
      <AppCard>{renderContent()}</AppCard>
    </AppPage>
  );
};
